import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CartAlerts } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/CartAlerts';
import { CustomCartAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/components/CustomCartAlert';
import { CartForm } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartForm/CartForm';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { ItemMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ItemMap';
import { CartFormSchema } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/CartFormSchema';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import {
  DialogStepContent,
  DialogStepProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/DialogStepContent';
import { useCheckoutSessionCart } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSessionCart';
import { areItemsEqual } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/steps/CartEdit/utils/areItemsEqual';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { Text } from '@/components/shared/components/Text';

export const DefaultCartEdit = (props: DialogStepProps) => {
  const { cart } = useCart();

  // This fill ensure that the use effect in CartForm that restores the cart items is triggered
  if (!cart?.items) {
    return null;
  }

  const defaultValues = { items: [] };

  return (
    <FormWrapper formSchema={CartFormSchema} defaultValues={defaultValues}>
      <DefaultCartEditForm {...props} />
    </FormWrapper>
  );
};

export const DefaultCartEditForm = ({ onNext, onPrev }: DialogStepProps) => {
  const { checkoutSession } = useCheckoutSessionCart();
  const { arePaymentMethodsAvailable, totalToFinance } = usePaymentMethods();
  const { cart, cartIsValid } = useCart();
  const { trigger } = useFormContext();

  const [areAlertsVisible, setAreAlertsVisible] = useState<boolean>(false);

  useEffect(() => {
    trigger();
  }, []);

  const cartHasChanges = useMemo(
    () =>
      // Could be improved: dates can pose a problem because of timezones etc.
      areItemsEqual(cart?.items, checkoutSession ? ItemMap.fromCheckoutSessionToItems(checkoutSession) : undefined),
    [cart, checkoutSession],
  );

  const cartHasPaymentMethodsAvailable = useMemo(
    () => cart && arePaymentMethodsAvailable(totalToFinance).state,
    [cart, arePaymentMethodsAvailable],
  );

  const hasAnyAlert = !cartIsValid || !cartHasPaymentMethodsAvailable || !cartHasChanges;

  const handleSubmit = () => {
    if (hasAnyAlert) {
      showAlerts();
      return;
    }

    onNext();
  };

  const showAlerts = () => {
    if (!areAlertsVisible) setAreAlertsVisible(true);
  };

  return (
    <DialogStepContent
      titleTextId={'CART_EDIT'}
      descriptionTextId={'CART_EDIT_INSTRUCTIONS'}
      prevButtonTextId={'CLOSE'}
      onNext={handleSubmit}
      onPrev={onPrev}
      disabled={areAlertsVisible && hasAnyAlert}
    >
      <CartForm />
      {areAlertsVisible && (
        <FlexColumn mt={2} alignItems={'flex-end'}>
          <FlexColumn gridGap={8} maxWidth={'420px'}>
            <CartAlerts />
            {cartIsValid && cartHasPaymentMethodsAvailable && !cartHasChanges && (
              <CustomCartAlert>
                <Text tId={'ALERT_CART_NO_CHANGES'} />
              </CustomCartAlert>
            )}
          </FlexColumn>
        </FlexColumn>
      )}
    </DialogStepContent>
  );
};
